import React from "react";
import styled from "styled-components";
import CaseStudiesCard from "../CaseStudiesCard";
import Links from "../../../Links";
import Rule from "../../../Rule";
import Heading from "../../../Heading";
import Img from "../../../../img/objective.png";
import Image from "../../../Image";
import Box from "../../../Box";
import BlogLayout from "../BlogLayout";
import Stack from "../../../Stack";

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-medium);
  @media (min-width: 700px) {
    gap: var(--spacing-large);
    grid-template-columns: 1fr 1fr;
  }
`;

const Outcomes = (
  <CaseStudiesCard id="outcomes">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Case Study
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Showcasing a design system objective and its impact on the products it informs.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout wide>
      <Stack spacing="var(--spacing-large)">
        <Box>
          <Heading variant="larger" style={{ marginBottom: 0 }}>
            Guru Outcomes
          </Heading>
          <p
            style={{
              fontWeight: "regular",
              fontSize: "var(--font-size-heading-small)",
              marginBottom: "var(--spacing-tiny)",
            }}
          >
            Improving Application Performance with Muibook
          </p>
        </Box>
        <Grid>
          <Box>
            <Heading variant="small">Objective</Heading>
            <p>Reduce runtime overhead to improve performance and make the app fast for users.</p>
            <Heading variant="small">Key Results</Heading>
            <p>
              Achieve at least a <strong>10% improvement in page load time</strong>.
            </p>
          </Box>
          <Box>
            <Heading variant="small">Problem</Heading>
            <p>
              We identified that{" "}
              <Links href="https://outcomes.gurusuite.xyz/" target="_blank" rel="noopener noreferrer">
                Guru Outcomes
              </Links>{" "}
              was slow to load initially. Since Emotion.js generates CSS at runtime, impacting browser performance, we
              hypothesised that replacing Emotion with MUI components for the core UI and CSS Modules for the app layout
              would improve performance.
            </p>
          </Box>
        </Grid>

        <Image dark src={Img} alt="Guru Outcomes App" caption="We tracked our task in Guru Outcomes" />

        <Grid>
          <Box>
            <Heading variant="small">Method</Heading>
            <ul>
              <li>
                <strong>Refactor:</strong> Converted all styled components to CSS Modules and MUI components where
                applicable, and removed Emotion dependencies.
              </li>
              <li>
                <strong>Baseline Measurement:</strong> Used Chrome DevTools Performance tab to measure total load time
                and key categories (scripting, loading, rendering, painting).
              </li>
              <li>
                <strong>Post-Change Measurement:</strong> Re-ran the same performance scenario in DevTools to compare
                results.
              </li>
            </ul>
          </Box>
          <Box>
            <Heading variant="small">Results</Heading>
            <ul>
              <li>
                Total page load time: <strong>2.6s → 1.4s</strong>
              </li>
              <li>
                Scripting time: <strong>~1.3s → ~0.3s</strong> (largest gain)
              </li>
              <li>
                Loading: <strong>~0.4s → ~0.04s</strong>
              </li>
              <li>Rendering & Painting: modest improvements</li>
              <li>
                Overall performance improvement: <strong>~45%, well above the 10% target</strong>
              </li>
            </ul>
          </Box>
        </Grid>

        <Grid>
          <Box>
            <Heading variant="small">Analysis</Heading>
            <ul>
              <li>Removing Emotion drastically reduced scripting overhead.</li>
              <li>Smaller bundle size cut loading costs by 90%.</li>
              <li>Rendering and painting improvements smoothed the user experience.</li>
            </ul>
          </Box>
          <Box>
            <Heading variant="small">Conclusion</Heading>
            <p>
              Migrating from Emotion to CSS Modules not only met but significantly exceeded the original{" "}
              <strong>10% performance goal</strong>. The app now loads over 1 second faster, delivering a faster,
              smoother experience while simplifying the styling approach.
            </p>
          </Box>
        </Grid>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default Outcomes;
